import { useTranslation } from 'react-i18next';
import { useCreateIssue, useSiteAreas, useSiteProfessions } from '@components/Pages/IssuesPage/hooks';
import { areaRejectionIssue } from '@d-orca/shared/client/issues';
import { OWNS_ERROR_FEEDBACK } from '@d-orca/shared/client/hooks';
import { ReviewDialog } from '@components/ui/ReviewDialog/ReviewDialog';
import type { ISchedulerTask, WritableLocationStatus } from '@interfaces/IScheduler';

/** The area under review: which task's, and which area. */
export interface IAreaUnderReview {
  task: Pick<ISchedulerTask, 'id' | 'profession'>;
  zoneId: string;
}

export interface IAreaReviewDialogProps {
  siteId: string;
  /** Rendered only while an area is under review. */
  reviewing: IAreaUnderReview;
  onClose: () => void;
  /** Writes the decision: `complete` (approved) or `planned` (out of review). */
  onDecide: (taskId: string, zoneId: string, status: WritableLocationStatus) => void;
}

/**
 * A manager's review of an area submitted for it (area review stage spec,
 * ruling 5). Approve reports it done. Reject raises an issue — the issue IS the
 * reason, as in Trusstor: linked to the task, of its profession, at the area —
 * and only once the issue exists takes the area out of review, so a failed
 * create leaves it waiting.
 */
export function AreaReviewDialog({ siteId, reviewing, onClose, onDecide }: IAreaReviewDialogProps) {
  const { t } = useTranslation();
  // A failed create is the dialog's to report (its reject error).
  const create = useCreateIssue(siteId, OWNS_ERROR_FEEDBACK);
  const areas = useSiteAreas(siteId).data?.areas;
  const professions = useSiteProfessions(siteId).data?.professions;

  const { task, zoneId } = reviewing;

  // Reject waits for the site's areas and professions: an issue raised without
  // them would carry no location and no trade — one no Limited user can see.
  const raiseIssue =
    areas && professions
      ? async (description: string) => {
          await create.mutateAsync(areaRejectionIssue(description, task, zoneId, areas, professions));
          onDecide(task.id, zoneId, 'planned');
        }
      : null;

  return (
    <ReviewDialog
      onClose={onClose}
      title={t('scheduler.panel.locations.reviewTitle')}
      reasonPlaceholder={t('scheduler.panel.locations.rejectIssuePlaceholder')}
      rejectError={t('scheduler.panel.locations.rejectError')}
      onApprove={() => onDecide(task.id, zoneId, 'complete')}
      onReject={raiseIssue}
    />
  );
}
